/**
 * App.tsx — The PHACKTS document orchestrator.
 *
 * This file contains ZERO content. Every visible word comes from
 * content.json via the BlockRenderer and SegmentRenderer pipeline.
 *
 * Architecture:
 *   content.json → App.tsx → Section → BlockRenderer → SegmentRenderer
 *                                                    → Callout, CodeBlock, Term
 *
 * This file is GPL-licensed software. content.json is private data.
 */

import { useState, useRef, useCallback } from 'react';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Search from './components/Search';
import BlockRenderer from './components/BlockRenderer';
import PrivacyModal from './components/PrivacyModal';
import LicenseModal from './components/LicenseModal';
import SearchFeatureModal from './components/SearchFeatureModal';
import PaletteBar from './components/PaletteBar';
import content from '../content.json';
import type { SiteContent, Section } from './types';

const data = content as unknown as SiteContent;

/**
 * Renders a single document section from content.json.
 */
function SectionView({ section, isFirst }: { section: Section; isFirst: boolean }) {
  const HeadingTag = section.isHero ? 'h1' : 'h2';
  const headingId = `${section.id}-heading`;

  return (
    <section
      id={section.id}
      className={`scroll-mt-32 ${isFirst ? '' : 'pt-12 border-t border-slate-900'}`}
      aria-labelledby={headingId}
    >
      {/* Tagline */}
      {section.tagline && (
        <div className="text-emerald-500 font-mono text-[11px] font-bold tracking-[0.2em] uppercase mb-4">
          {section.tagline}
        </div>
      )}

      {/* Heading */}
      <HeadingTag
        id={headingId}
        className={
          section.isHero
            ? 'text-5xl font-extrabold text-white mb-8 tracking-tight'
            : 'text-3xl font-bold text-white mb-8'
        }
      >
        {section.title}
      </HeadingTag>

      {/* Blocks */}
      <div className={section.isHero ? 'text-xl max-w-3xl' : 'space-y-6'}>
        <BlockRenderer blocks={section.blocks} />
      </div>
    </section>
  );
}

export default function App() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  const [isLicenseOpen, setIsLicenseOpen] = useState(false);
  const [isSearchFeatureOpen, setIsSearchFeatureOpen] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scrollToTop = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="flex h-screen overflow-hidden bg-slate-950 selection:bg-emerald-500/20 selection:text-emerald-300">
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className="flex flex-1 flex-col overflow-hidden">
        <Header
          onMenuClick={() => setIsSidebarOpen(true)}
          onSearchOpen={() => setIsSearchOpen(true)}
        />

        <main
          ref={scrollContainerRef}
          id="main-content"
          className="flex-1 overflow-y-auto scroll-smooth custom-scrollbar relative"
          tabIndex={-1}
        >
          <div className="main-content-wrapper mx-auto max-w-5xl px-4 py-24 lg:px-12">
            <div className="space-y-20">
              {/* ── Content Sections ── */}
              {data.sections.map((section, i) => (
                <SectionView key={section.id} section={section} isFirst={i === 0} />
              ))}

              {/* ── Back to Top ── */}
              <div id="back-to-top" className="pt-8 flex justify-center pb-8 border-t border-slate-900">
                <button
                  type="button"
                  onClick={scrollToTop}
                  className="group flex items-center gap-3 px-8 py-4 rounded-full bg-slate-900 border border-slate-800 text-slate-400 hover:text-emerald-400 hover:border-emerald-500/50 transition-all shadow-2xl"
                >
                  <svg className="w-4 h-4 group-hover:-translate-y-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                  </svg>
                  <span className="text-xs font-bold uppercase tracking-widest">{data.meta.backToTopLabel}</span>
                </button>
              </div>

              {/* ── Footer ── */}
              <footer className="py-16 border-t border-slate-900 space-y-8">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
                  <div className="space-y-2">
                    <div className="text-white font-bold text-lg tracking-tight font-display">
                      {data.footer.brand}
                    </div>
                    <p className="text-[11px] font-medium text-slate-500 leading-relaxed">
                      Created by{' '}
                      <a
                        href={data.footer.authorUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-emerald-500 hover:underline font-bold inline-flex items-center gap-1"
                      >
                        {data.footer.author}
                        <svg className="w-[10px] h-[10px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-x-10 gap-y-4 text-sm font-medium text-slate-400">
                    {data.footer.links.map((link) => {
                      // Privacy and License are buttons that open modals
                      if (link.href === '#privacy') {
                        return (
                          <button
                            key={link.label}
                            type="button"
                            onClick={() => setIsPrivacyOpen(true)}
                            className="hover:text-white transition-colors flex items-center gap-2"
                          >
                            <svg className="w-[14px] h-[14px] text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                            {link.label}
                          </button>
                        );
                      }
                      if (link.href === '#license') {
                        return (
                          <button
                            key={link.label}
                            type="button"
                            onClick={() => setIsLicenseOpen(true)}
                            className="hover:text-white transition-colors flex items-center gap-2"
                          >
                            <svg className="w-[14px] h-[14px] text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                            </svg>
                            {link.label}
                          </button>
                        );
                      }
                      return (
                        <a
                          key={link.label}
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-white transition-colors"
                        >
                          {link.label}
                        </a>
                      );
                    })}
                  </div>
                </div>

                {/* ── Palette Bar (dev/preview only) ── */}
                <PaletteBar />
              </footer>
            </div>
          </div>
        </main>
      </div>

      {/* Modals & Overlays — client-side enhancement only */}
      <Search isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} onSearchFeatureOpen={() => setIsSearchFeatureOpen(true)} />
      {isPrivacyOpen && <PrivacyModal onClose={() => setIsPrivacyOpen(false)} />}
      {isLicenseOpen && <LicenseModal onClose={() => setIsLicenseOpen(false)} />}
      {isSearchFeatureOpen && <SearchFeatureModal onClose={() => setIsSearchFeatureOpen(false)} />}
    </div>
  );
}
